import React from 'react';
import { useTranslation } from 'react-i18next';
import { ModalShell } from '../../../ui/ModalShell/ModalShell';
import { Tabs } from '../../../ui/Tabs/Tabs';
import { Banner } from '../../../ui/Banner/Banner';
import { Button } from '../../../ui/Button/Button';
import { FormInput } from '../../../ui/FormInput/FormInput';
import { TextArea } from '../../../ui/TextArea/TextArea';
import { ValidationMessage } from '../../../components/Forms/ValidationMessage';
import { TranslationKey } from '../../../i18n/translationKeys';
import { TEMPLATE_LANGUAGES } from '../../../types/customEmailTemplate';
import type { CustomEmailTemplateSetting, TemplateLanguage } from '../../../types/customEmailTemplate';
import { MacroInsertButtons } from './MacroInsertButtons';
import { MAX_BODY_LENGTH, MAX_SUBJECT_LENGTH } from './customTemplateValidation';
import type { FieldError } from './customTemplateValidation';
import { FIELD_IDS, useCustomEmailTemplateEditor } from './useCustomEmailTemplateEditor';

interface CustomEmailTemplateModalProps {
    /** The template to edit, macros in canonical form; null to start from blank fields. */
    initial: CustomEmailTemplateSetting | null;
    initialLanguage: TemplateLanguage;
    onClose: () => void;
    onSaved?: (saved: CustomEmailTemplateSetting) => void;
}

const PANEL_CLASSNAME = 'bg-surface-elevated ring-1 ring-line-subtle rounded-lg shadow-strong w-full max-w-dialogue mx-l p-xl flex flex-col gap-l max-h-full overflow-y-auto';

const LANGUAGE_LABELS: Record<TemplateLanguage, string> = {
    FR: TranslationKey.CustomEmailTemplate.Languages.Fr,
    EN: TranslationKey.CustomEmailTemplate.Languages.En,
};

export const CustomEmailTemplateModal: React.FC<CustomEmailTemplateModalProps> = ({ initial, initialLanguage, onClose, onSaved }) => {
    const { t } = useTranslation();
    const editor = useCustomEmailTemplateEditor(initial, initialLanguage, saved => {
        onSaved?.(saved);
        onClose();
    });

    const fieldMessage = (error: FieldError | undefined, required: string, tooLong: string, max: number) =>
        error === 'required' ? t(required) : error === 'tooLong' ? t(tooLong, { max }) : undefined;
    const subjectError = fieldMessage(editor.errors.subject, TranslationKey.CustomEmailTemplate.Modal.Errors.SubjectRequired,
        TranslationKey.CustomEmailTemplate.Modal.Errors.SubjectTooLong, MAX_SUBJECT_LENGTH);
    const bodyError = fieldMessage(editor.errors.body, TranslationKey.CustomEmailTemplate.Modal.Errors.BodyRequired,
        TranslationKey.CustomEmailTemplate.Modal.Errors.BodyTooLong, MAX_BODY_LENGTH);
    const formErrorKey = editor.noLanguage
        ? TranslationKey.CustomEmailTemplate.Modal.Errors.AtLeastOneLanguage
        : editor.saveError === 'invalid' ? TranslationKey.CustomEmailTemplate.Modal.Errors.Invalid
            : editor.saveError === 'failed' ? TranslationKey.CustomEmailTemplate.Modal.Errors.SaveFailed
                : null;

    return (
        <ModalShell
            heading={t(TranslationKey.CustomEmailTemplate.Modal.Heading)}
            onClose={onClose}
            dismissAriaLabel={t(TranslationKey.CustomEmailTemplate.Modal.Cancel)}
            rootTestId="custom-email-template-modal"
            panelClassName={PANEL_CLASSNAME}
            footer={
                <>
                    <Button type="button" variant="secondary" onClick={onClose} data-testid="custom-email-template-cancel">
                        {t(TranslationKey.CustomEmailTemplate.Modal.Cancel)}
                    </Button>
                    <Button type="button" loading={editor.saving} onClick={() => void editor.submit()} data-testid="custom-email-template-save">
                        {t(TranslationKey.CustomEmailTemplate.Modal.Save)}
                    </Button>
                </>
            }
        >
            <Banner tone="info" testId="custom-email-template-company-wide">
                {t(TranslationKey.CustomEmailTemplate.Modal.CompanyWide)} {t(TranslationKey.CustomEmailTemplate.Modal.LanguageHint)}
            </Banner>
            <Tabs
                activeIndex={TEMPLATE_LANGUAGES.indexOf(editor.language)}
                onTabChange={index => editor.setLanguage(TEMPLATE_LANGUAGES[index])}
                tabs={TEMPLATE_LANGUAGES.map(language => ({
                    label: t(LANGUAGE_LABELS[language]),
                    testId: `custom-email-template-tab-${language}`,
                }))}
            />
            <div className="flex flex-col gap-m">
                <div>
                    <FormInput
                        id={FIELD_IDS.subject}
                        data-testid="custom-email-template-subject"
                        label={t(TranslationKey.CustomEmailTemplate.Modal.Subject)}
                        value={editor.draft.subject}
                        onChange={e => editor.setField('subject', e.target.value)}
                        onFocus={() => editor.focusField('subject')}
                        aria-invalid={!!subjectError}
                    />
                    <ValidationMessage data-testid="custom-email-template-subject-error" message={subjectError} />
                </div>
                <div>
                    <TextArea
                        id={FIELD_IDS.body}
                        data-testid="custom-email-template-body"
                        label={t(TranslationKey.CustomEmailTemplate.Modal.Body)}
                        value={editor.draft.body}
                        onChange={e => editor.setField('body', e.target.value)}
                        onFocus={() => editor.focusField('body')}
                        rows={12}
                        aria-invalid={!!bodyError}
                    />
                    <ValidationMessage data-testid="custom-email-template-body-error" message={bodyError} />
                </div>
                <MacroInsertButtons language={editor.language} onInsert={editor.insertMacro} disabled={editor.saving} />
                <ValidationMessage data-testid="custom-email-template-form-error" message={formErrorKey ? t(formErrorKey) : undefined} />
            </div>
        </ModalShell>
    );
};
